import React, { useState, useEffect } from 'react';
import { Calendar, ChevronRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../config/apiConfig';

const SeriesCalendarWidget = () => {
    const [episodes, setEpisodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUpcomingEpisodes();
    }, []);

    const fetchUpcomingEpisodes = async () => {
        try {
            const adminToken = localStorage.getItem('cinestream_token');
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/series-calendar/upcoming-episodes`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': adminToken
                }
            });
            const data = await response.json();

            if (data.success) {
                // Afficher les séries futures ET en attente, limité à 8
                const toShow = data.episodes
                    .filter(ep => !ep.is_past || ep.is_waiting)
                    .slice(0, 8);
                setEpisodes(toShow);
            }
        } catch (err) {
            console.error('Erreur fetch épisodes:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatRelativeDate = (episode) => {
        // Gérer les séries en attente
        if (episode.is_waiting) return "En attente";

        if (episode.is_today) return "Aujourd'hui";
        if (episode.days_until === 1) return "Demain";
        if (episode.days_until === 2) return "Après-demain";
        if (episode.days_until <= 7) return `Dans ${episode.days_until}j`;
        return episode.air_date_formatted;
    };

    const getBadgeColor = (episode) => {
        // Badge gris pour les séries en attente
        if (episode.is_waiting) return 'bg-gray-600';

        if (episode.is_today) return 'bg-red-500 animate-pulse';
        if (episode.days_until === 1) return 'bg-orange-500';
        if (episode.is_this_week) return 'bg-blue-500';
        return 'bg-purple-500';
    };

    if (loading || episodes.length === 0) return null;

    const badgeStyle = (episode) => {
        if (episode.is_waiting) return { background: 'rgba(107,114,128,0.85)' };
        if (episode.is_today) return { background: 'rgba(239,68,68,0.9)', animation: 'glowPulse 2s infinite' };
        if (episode.days_until === 1) return { background: 'rgba(249,115,22,0.85)' };
        if (episode.is_this_week) return { background: 'rgba(59,130,246,0.85)' };
        return { background: 'rgba(232,183,90,0.85)' };
    };

    return (
        <div style={{ maxWidth: 'var(--max-w)', margin: '32px auto', padding: '0 20px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Calendar style={{ width: 22, height: 22, color: 'var(--accent)' }} />
                    <div>
                        <h2 className="section-title" style={{ marginBottom: 0 }}>Prochaines Sorties</h2>
                        <p style={{ color: 'var(--text-4)', fontSize: '0.8125rem', marginTop: 3 }}>
                            {episodes.length} épisode{episodes.length > 1 ? 's' : ''} à venir
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/calendrier')}
                    className="btn-ghost"
                    style={{ padding: '7px 14px', fontSize: '0.875rem', gap: 5 }}
                >
                    <span>Voir tout</span>
                    <ChevronRight style={{ width: 14, height: 14 }} />
                </button>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8" style={{ gap: 12 }}>
                {episodes.map((episode) => (
                    <div
                        key={episode.id}
                        onClick={() => navigate('/calendrier')}
                        className="movie-card"
                        style={{
                            background: 'var(--glass)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: 'var(--r-md)',
                            overflow: 'hidden',
                            cursor: 'pointer',
                        }}
                    >
                        <div style={{ position: 'relative', aspectRatio: '2/3', background: 'var(--bg-elevated)' }}>
                            {episode.poster ? (
                                <img
                                    src={episode.poster}
                                    alt={episode.series_name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 300ms ease' }}
                                />
                            ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Calendar style={{ width: 24, height: 24, color: 'var(--text-4)', opacity: 0.4 }} />
                                </div>
                            )}
                            <div style={{ position: 'absolute', top: 4, left: 4, right: 4 }}>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    padding: '2px 6px', borderRadius: 'var(--r-sm)',
                                    color: '#fff', fontSize: '0.5625rem', fontWeight: 700,
                                    backdropFilter: 'blur(6px)', whiteSpace: 'nowrap',
                                    ...badgeStyle(episode),
                                }}>
                                    <Clock style={{ width: 8, height: 8 }} />
                                    {formatRelativeDate(episode)}
                                </span>
                            </div>
                        </div>
                        <div style={{ padding: '6px 8px' }}>
                            <h3 style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.6875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                                {episode.series_name}
                            </h3>
                            <p style={{ color: 'var(--accent-bright)', fontSize: '0.625rem', fontWeight: 600 }}>
                                S{episode.season}E{episode.episode}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SeriesCalendarWidget;